import {
  BodyText,
  Button,
  Callout,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { Fragment, useState } from "react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { SurgicalHistoryForm } from "./components/SurgicalHistoryForm";
import { SurgicalHistoryItem } from "./components/SurgicalHistoryItem";
import { SurgicalHistorySkeleton } from "./components/SurgicalHistorySkeleton";
import { useAddSurgicalHistory, useGetSurgicalHistory } from "./network";

export const SurgicalHistory = () => {
  const { patientId } = useParams({ strict: false });
  const [isAddingSurgicalHistory, setIsAddingSurgicalHistory] = useState(false);
  const {
    data,
    isLoading: isLoadingSurgicalHistory,
    isError,
    refetch,
  } = useGetSurgicalHistory({ patientId: patientId as string });
  const { mutate: addSurgicalHistory, isPending: isSubmitting } =
    useAddSurgicalHistory({
      patientId: patientId as string,
      onSuccess: () => {
        toast.success("Surgical history added successfully");
        setIsAddingSurgicalHistory(false);
      },
      onError: (error) => {
        toast.error(`Failed to add surgical history: ${error.message}`);
      },
    });

  const showSkeleton = isLoadingSurgicalHistory;
  const showError = !showSkeleton && (isError || !data);
  const showEmpty =
    !showSkeleton &&
    !showError &&
    !isAddingSurgicalHistory &&
    data &&
    data.surgicalHistory.length === 0;
  const showSurgicalHistory =
    !showSkeleton && !showError && data && data.surgicalHistory.length > 0;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Surgical History"
        actionButtons={[
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => {
                trackEvent(TRACKERS.ADD_SURGICAL_HISTORY);
                setIsAddingSurgicalHistory(true);
              }}
              aria-label="Add surgical history"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {isAddingSurgicalHistory && (
          <>
            <SurgicalHistoryForm
              onCancel={() => {
                trackEvent(TRACKERS.CANCEL_SURGICAL_HISTORY);
                setIsAddingSurgicalHistory(false);
              }}
              onSubmit={(data) => {
                trackEvent(TRACKERS.SAVE_SURGICAL_HISTORY);
                addSurgicalHistory({
                  patientId: patientId as string,
                  performedDate: data.performedDate.toISOString(),
                  procedureCode: {
                    coding: [
                      {
                        code: data.procedureCode,
                        display: data.procedureCodeDisplay,
                        system: data.procedureCodeSystem,
                      },
                    ],
                    text: data.procedureCodeDisplay,
                  },
                });
              }}
              isSubmitting={isSubmitting}
            />
            <Separator className="w-full" />
          </>
        )}
        {showSkeleton && <SurgicalHistorySkeleton />}
        {showError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load surgical history!"
              description="Please click here to try again."
              onClick={() => refetch()}
            />
          </div>
        )}
        {showEmpty && (
          <div className="flex py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No surgical history documented
            </BodyText>
          </div>
        )}
        {showSurgicalHistory && (
          <div>
            {data.surgicalHistory.map((surgicalHistory) => (
              <Fragment key={surgicalHistory.id}>
                <SurgicalHistoryItem
                  key={surgicalHistory.id}
                  surgicalHistory={surgicalHistory}
                />
                <Separator className="w-full" />
              </Fragment>
            ))}
          </div>
        )}
      </SectionContent>
    </div>
  );
};
